import type { AxiosRequestConfig } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import { responseContract, transformOptionalString } from '../../lib';
import {
  CareStructureEnum,
  FacilityTypeEnum,
  FormStatusEnum,
  MarketFacilityCreateCareLevelDtoSchema,
  MarketFacilityCreateExtraServiceDtoSchema,
  MarketFacilityCreateRoomTypeDtoSchema,
  MarketFacilityFormResponseDtoSchema,
  MarketPositionEnum,
} from '../contracts';

const CreateMarketFacilityFormRequestDtoSchema = z.object({
  coordinates: z.object({
    lat: z.number().nullable(),
    lng: z.number().nullable(),
  }),
  dba: z.string(),
  facilityName: z.string(),
  administrator: z
    .string()
    .default('')
    .optional()
    .transform(transformOptionalString),
  admissionsContact: z
    .string()
    .default('')
    .optional()
    .transform(transformOptionalString),
  amenities: z.array(z.number()).optional(),
  awards: z.array(z.string()).optional(),
  careStructure: CareStructureEnum.optional(),
  collectedAt: z.string().optional(),
  collectedBy: z.number().optional(),
  extraServices: z.array(MarketFacilityCreateExtraServiceDtoSchema).optional(),
  interestedCustomer: z.boolean().default(false).optional(),
  levelsOfCare: z.array(MarketFacilityCreateCareLevelDtoSchema).optional(),
  marketFacilityId: z.number().optional(),
  marketingContact: z
    .string()
    .default('')
    .optional()
    .transform(transformOptionalString),
  marketPosition: MarketPositionEnum.optional(),
  medicaid: z.boolean().default(false).optional(),
  moveInFee: z.number().optional(),
  moveInRefundable: z.boolean().default(false).optional(),
  notes: z.string().optional().transform(transformOptionalString),
  ownerName: z
    .string()
    .default('')
    .optional()
    .transform(transformOptionalString),
  phoneNumber: z.string().optional().transform(transformOptionalString),
  roomTypes: z.array(MarketFacilityCreateRoomTypeDtoSchema).optional(),
  secureBeds: z.number().optional(),
  skilledNursingOffered: z.boolean().default(false).optional(),
  sourceEmailWithFacility: z.boolean().default(false).optional(),
  sourceFacilityStaffCall: z.boolean().default(false).optional(),
  sourceFacilityWebsite: z.boolean().default(false).optional(),
  sourceGoogleSearch: z.boolean().default(false).optional(),
  sourceMarketingBrochure: z.boolean().default(false).optional(),
  sourceOnSiteVisit: z.boolean().default(false).optional(),
  sourceOther: z.boolean().default(false).optional(),
  sourcePublicListing: z.boolean().default(false).optional(),
  sourceStateDb: z.boolean().default(false).optional(),
  status: FormStatusEnum.default('DRAFT').optional(),
  totalBeds: z.number().optional(),
  types: z.array(FacilityTypeEnum).optional(),
  typicalStaffToResidentRatio: z.number().optional(),
  website: z.string().optional().transform(transformOptionalString),
  yearBuilt: z.number().optional(),
});

const CreateMarketFacilityFormResponseDtoSchema =
  MarketFacilityFormResponseDtoSchema;

export type CreateMarketFacilityFormRequestDto = z.infer<
  typeof CreateMarketFacilityFormRequestDtoSchema
>;
export type CreateMarketFacilityFormResponseDto = z.infer<
  typeof CreateMarketFacilityFormResponseDtoSchema
>;

export const createMarketFacilityForm = (
  dto: CreateMarketFacilityFormRequestDto,
  config?: AxiosRequestConfig<CreateMarketFacilityFormRequestDto>
) => {
  const data = CreateMarketFacilityFormRequestDtoSchema.parse(dto);
  return api
    .post('/market-facilities/form', data, config)
    .then(responseContract(CreateMarketFacilityFormResponseDtoSchema));
};
